import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import GridLayout, { Layout, LayoutItem, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useDashboardLayout, useUpdateDashboardLayout, useSaveDashboardLayout, useResetDashboardLayoutToDefault, type WidgetConfig, type DashboardGroup } from './hooks/useDashboardLayout';
import { getWidgetDefinition, createWidgetElement } from './widgetRegistry';
import DraggableWidget from './components/DraggableWidget';
import DashboardSettings from './components/DashboardSettings';
import DashboardFilterBar from './components/DashboardFilterBar';
import SaveAsDashboardModal from './components/SaveAsDashboardModal';
import { useDashboardFilters } from './contexts/DashboardFiltersContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useConfirm } from '../../components/ui';
import { useNotification } from '../../contexts/NotificationContext';

function widgetTitle(w: { widgetType: string; settings?: Record<string, unknown> }): string {
  const pageName = w.settings?.pageName;
  if (typeof pageName === 'string' && pageName.trim()) return pageName.trim();
  return getWidgetDefinition(w.widgetType)?.name ?? w.widgetType;
}

export interface DynamicDashboardProps {
  layoutName?: string;
}
const ROW_HEIGHT = 88;
/** [horizontal gap between widgets, vertical gap] */
const MARGIN: [number, number] = [24, 12];

const GRID_DOT_STEP = 34;

/** Responsive column count — never exceeds canonical maxCols; shrinks on narrow screens. */
function getResponsiveCols(containerWidth: number, maxCols: number): number {
  if (containerWidth <= 0) return maxCols;
  if (containerWidth < 640) return Math.min(4, maxCols);
  if (containerWidth < 960) return Math.min(6, maxCols);
  if (containerWidth < 1280) return Math.min(9, maxCols);
  return maxCols;
}

/** Scale layout from current (responsive) cols to canonical cols for saving (keeps layout consistent across breakpoints). */
function scaleLayoutToCols(layout: Layout, fromCols: number, toCols: number): Layout {
  if (fromCols === toCols) return layout;
  const scale = toCols / fromCols;
  return layout.map((item) => ({
    ...item,
    x: Math.min(Math.round(item.x * scale), toCols - 1),
    y: item.y,
    w: Math.min(Math.max(1, Math.round(item.w * scale)), toCols),
    h: item.h,
  })) as Layout;
}

interface DashboardSection {
  groupId: string;
  title: string;
  widgets: WidgetConfig[];
}

const DynamicDashboard: React.FC<DynamicDashboardProps> = ({ layoutName }) => {
  const isMobile = false;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, colors } = useTheme();
  const { appConfig } = useSettings();
  const COLUMNS = appConfig.dashboard.gridColumns;
  const isDark = theme === 'dark';
  const { width, containerRef, mounted, measureWidth } = useContainerWidth({
    initialWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
  });
  const responsiveCols = useMemo(() => getResponsiveCols(width, COLUMNS), [width, COLUMNS]);
  const { layout, isLoading, mutate } = useDashboardLayout(layoutName);

  // Re-measure container width after layout settles and on window resize so grid reflows correctly
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        measureWidth?.();
      });
    });
    const t = setTimeout(() => measureWidth?.(), 100);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [measureWidth]);

  const saveLayout = useSaveDashboardLayout();
  const updateLayout = useUpdateDashboardLayout();
  const resetLayoutToDefault = useResetDashboardLayoutToDefault();
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotification();
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();

  // When user selects a different dashboard (URL/layoutName changes), reload from backend (DashboardLayout)
  React.useEffect(() => {
    mutate();
  }, [layoutName, mutate]);

  /** Revalidate dashboard layout and all widget data (stats, lists, charts, etc.) */
  const handleRefresh = useCallback(() => {
    mutate();
    globalMutate((key) => typeof key === 'string' && key.startsWith('/dashboard/'));
  }, [mutate, globalMutate]);

  /** Dot grid background when in customize mode - visible in light and dark */
  const gridDotStyle: React.CSSProperties = useMemo(
    () =>
      isDark
        ? {
            backgroundImage: 'radial-gradient(circle at center, rgba(203, 213, 225, 0.4) 1px, transparent 1px)',
            backgroundSize: `${GRID_DOT_STEP}px ${GRID_DOT_STEP}px`,
          }
        : {
            backgroundImage: 'radial-gradient(circle at center, rgba(148, 163, 184, 0.5) 1px, transparent 1px)',
            backgroundSize: `${GRID_DOT_STEP}px ${GRID_DOT_STEP}px`,
          },
    [isDark]
  );
  const { timeRange, scope } = useDashboardFilters();
  const [isEditing, setIsEditing] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dashboardFilters = useMemo(() => ({ timeRange, scope }), [timeRange, scope]);

  // When switching dashboard by name, only use layout if it matches the requested name (avoid showing wrong widgets)
  const layoutMatchesRequest =
    !layoutName || !layout?.name || layout.name === layoutName;
  const effectiveLayout = layoutMatchesRequest ? layout : undefined;
  const effectiveWidgets: WidgetConfig[] = useMemo(() => {
    if (!effectiveLayout?.widgets || !Array.isArray(effectiveLayout.widgets)) return [];
    return effectiveLayout.widgets.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [effectiveLayout?.widgets]);
  const effectiveGroups: DashboardGroup[] = useMemo(() => {
    const g = effectiveLayout?.groups;
    return Array.isArray(g) ? g : [];
  }, [effectiveLayout?.groups]);
  const effectiveSections: DashboardSection[] = useMemo(() => {
    const result: DashboardSection[] = [];
    for (const g of effectiveGroups) {
      result.push({
        groupId: g.id,
        title: g.title,
        widgets: effectiveWidgets.filter((w) => w.groupId === g.id),
      });
    }
    const ungrouped = effectiveWidgets.filter((w) => !w.groupId);
    if (ungrouped.length > 0) {
      result.push({ groupId: '', title: 'Other widgets', widgets: ungrouped });
    }
    return result;
  }, [effectiveGroups, effectiveWidgets]);

  const handleResetLayout = useCallback(async () => {
    if (!effectiveLayout?.id) return;
    const ok = await confirm({
      title: 'Reset layout to default',
      message: `Replace widgets on "${effectiveLayout.name}" with the standard full-width SCMS layout? Your current arrangement will be lost.`,
      warningMessage: 'Custom widget sizes, positions, and groups on this dashboard will be overwritten.',
      confirmText: 'Reset layout',
      cancelText: 'Cancel',
      confirmVariant: 'warning',
    });
    if (!ok) return;
    try {
      await resetLayoutToDefault(effectiveLayout.id);
      await mutate();
      setIsEditing(false);
      showSuccess('Dashboard layout reset to default');
    } catch {
      showError('Failed to reset dashboard layout');
    }
  }, [confirm, effectiveLayout?.id, effectiveLayout?.name, mutate, resetLayoutToDefault, showError, showSuccess]);

  const handleSaveAsDashboard = useCallback(
    (name: string) => {
      const widgets = effectiveLayout?.widgets ?? layout?.widgets ?? [];
      const groups = effectiveLayout?.groups ?? layout?.groups ?? [];
      saveLayout({ name, widgets, groups, isDefault: false })
        .then(() => {
          const next = new URLSearchParams(searchParams);
          next.set('dashboard', name);
          navigate({ pathname: '/dashboard', search: next.toString() });
        })
        .catch(() => {});
    },
    [effectiveLayout?.widgets, effectiveLayout?.groups, layout?.widgets, layout?.groups, saveLayout, navigate, searchParams]
  );

  const gridLayout: Layout = useMemo(
    () =>
      effectiveWidgets.map((w) => ({
        i: w.id,
        x: w.position?.x ?? 0,
        y: w.position?.y ?? 0,
        w: w.size?.w ?? 2,
        h: w.size?.h ?? 2,
      })),
    [effectiveWidgets]
  ) as Layout;

  /** If a layout was authored for half the grid (legacy 6-col on a 12-col grid), scale it up to fill. */
  const layoutColumnSpan = useMemo(
    () =>
      effectiveWidgets.reduce(
        (max, w) => Math.max(max, (w.position?.x ?? 0) + (w.size?.w ?? 0)),
        0
      ),
    [effectiveWidgets]
  );

  const filledLayout: Layout = useMemo(() => {
    if (layoutColumnSpan > 0 && layoutColumnSpan * 2 === COLUMNS) {
      return scaleLayoutToCols(gridLayout, layoutColumnSpan, COLUMNS);
    }
    return gridLayout;
  }, [gridLayout, layoutColumnSpan, COLUMNS]);

  /** Scale stored (canonical) layout into the current responsive column count so widgets fill the row. */
  const displayLayout: Layout = useMemo(
    () => scaleLayoutToCols(filledLayout, COLUMNS, responsiveCols),
    [filledLayout, COLUMNS, responsiveCols]
  );

  const onLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!effectiveLayout?.id || !isEditing) return;
      const scaled = scaleLayoutToCols(newLayout as LayoutItem[], responsiveCols, COLUMNS);
      const layoutArray = Array.from(scaled) as LayoutItem[];
      const updated: WidgetConfig[] = effectiveWidgets.map((w) => {
        const item = layoutArray.find((l: LayoutItem) => l.i === w.id);
        if (!item) return w;
        return {
          ...w,
          position: { x: item.x, y: item.y },
          size: { w: item.w, h: item.h },
        };
      });
      updateLayout(effectiveLayout!.id, { widgets: updated }).catch(() => {});
    },
    [effectiveLayout?.id, isEditing, effectiveWidgets, updateLayout, responsiveCols, COLUMNS]
  );

  const onSectionLayoutChange = useCallback(
    (sectionWidgets: WidgetConfig[], newLayout: Layout) => {
      if (!effectiveLayout?.id || !isEditing) return;
      const scaled = scaleLayoutToCols(newLayout as LayoutItem[], responsiveCols, COLUMNS);
      const layoutArray = Array.from(scaled) as LayoutItem[];
      const sectionIds = new Set(sectionWidgets.map((w) => w.id));
      const updated: WidgetConfig[] = effectiveWidgets.map((w) => {
        if (!sectionIds.has(w.id)) return w;
        const item = layoutArray.find((l: LayoutItem) => l.i === w.id);
        if (!item) return w;
        return {
          ...w,
          position: { x: item.x, y: item.y },
          size: { w: item.w, h: item.h },
        };
      });
      updateLayout(effectiveLayout!.id, { widgets: updated }).catch(() => {});
    },
    [effectiveLayout?.id, isEditing, effectiveWidgets, updateLayout, responsiveCols, COLUMNS]
  );

  const handleRemove = useCallback(
    (widgetId: string) => {
      if (!effectiveLayout?.id) return;
      const updated = effectiveWidgets.filter((w) => w.id !== widgetId);
      updateLayout(effectiveLayout.id, { widgets: updated }).then(() => mutate()).catch(() => {});
    },
    [effectiveLayout?.id, effectiveWidgets, updateLayout, mutate]
  );

  const handleSettingsClick = useCallback((widgetId: string) => {
    setEditingWidgetId(widgetId);
    setSettingsOpen(true);
  }, []);

  const openSettings = useCallback(() => {
    setEditingWidgetId(null);
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    setEditingWidgetId(null);
  }, []);

  const dragHandle = (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" />
    </svg>
  );

  if (isLoading && !effectiveLayout) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <div className={`animate-pulse ${colors.textMuted}`}>Loading dashboard...</div>
      </div>
    );
  }

  const hasWidgets = effectiveWidgets.length > 0;

  return (
    <div className="h-full flex flex-col min-h-0 w-full min-w-0">
      <DashboardFilterBar
        currentLayoutName={layoutName}
        onSaveAsDashboard={() => setSaveAsOpen(true)}
        onManageDashboards={() => setSettingsOpen(true)}
        onResetLayout={effectiveLayout?.id ? handleResetLayout : undefined}
        onRefresh={handleRefresh}
        isCustomizing={isEditing}
        onCustomizeToggle={() => setIsEditing((e) => !e)}
        onOpenSettings={openSettings}
      />
      <SaveAsDashboardModal
        isOpen={saveAsOpen}
        onClose={() => setSaveAsOpen(false)}
        onConfirm={handleSaveAsDashboard}
        suggestedName={effectiveLayout?.name ? `${effectiveLayout.name} (copy)` : layout?.name ? `${layout.name} (copy)` : ''}
      />
      <div className="flex-1 min-h-0 overflow-auto dashboard-scrollbar w-full min-w-0 pt-5 sm:pt-6 flex flex-col">
      {!hasWidgets ? (
        <div
          className={`p-8 text-center border border-dashed rounded-xl min-h-[300px] flex items-center justify-center ${colors.textMuted} ${isEditing ? colors.border : ''}`}
          style={isEditing ? gridDotStyle : undefined}
        >
          <p>No widgets yet. Use the buttons below to add widgets or customize.</p>
        </div>
      ) : isMobile ? (
        <div className="space-y-6 px-2 pb-6 w-full min-w-0">
          {effectiveGroups.length > 0
            ? effectiveSections.map((sec) => (
                <div key={sec.groupId || 'other'}>
                  <h2 className={`text-sm font-semibold mb-3 mt-5 first:mt-0 ${colors.textMuted}`}>{sec.title}</h2>
                  <div className="space-y-5">
                    {sec.widgets.map((w) => {
                      const def = getWidgetDefinition(w.widgetType);
                      const title = widgetTitle(w);
                      const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                      return (
                        <div key={w.id}>
                          <DraggableWidget
                            id={w.id}
                            title={title}
                            isEditing={isEditing}
                            onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                            onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                            dashboardFilterSupport={def?.dashboardFilterSupport}
                          >
                            {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                          </DraggableWidget>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            : effectiveWidgets.map((w) => {
                const def = getWidgetDefinition(w.widgetType);
                const title = widgetTitle(w);
                const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                return (
                  <div key={w.id}>
                    <DraggableWidget
                      id={w.id}
                      title={title}
                      isEditing={isEditing}
                      onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                      onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                      dashboardFilterSupport={def?.dashboardFilterSupport}
                    >
                      {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                    </DraggableWidget>
                  </div>
                );
              })}
        </div>
      ) : effectiveGroups.length > 0 ? (
        <div
          ref={containerRef as React.RefObject<HTMLDivElement>}
          className={`flex-1 min-h-full space-y-8 rounded-xl w-full min-w-0 ${isEditing ? 'py-4' : ''}`}
          style={isEditing ? gridDotStyle : undefined}
        >
          {effectiveSections.map((sec) => {
            const rawSectionLayout: Layout = sec.widgets.map((w) => ({
              i: w.id,
              x: w.position?.x ?? 0,
              y: w.position?.y ?? 0,
              w: w.size?.w ?? 2,
              h: w.size?.h ?? 2,
            })) as Layout;
            const sectionSpan = sec.widgets.reduce(
              (max, w) => Math.max(max, (w.position?.x ?? 0) + (w.size?.w ?? 0)),
              0
            );
            const filledSectionLayout =
              sectionSpan > 0 && sectionSpan * 2 === COLUMNS
                ? scaleLayoutToCols(rawSectionLayout, sectionSpan, COLUMNS)
                : rawSectionLayout;
            const sectionLayout: Layout = scaleLayoutToCols(filledSectionLayout, COLUMNS, responsiveCols);
            return (
              <section key={sec.groupId || 'other'} className="min-h-[120px]">
                <h2 className={`text-sm font-semibold mb-4 px-1 ${colors.textSecondary}`}>{sec.title}</h2>
                {sec.widgets.length === 0 ? null : mounted && width > 0 ? (
                  <GridLayout
                    className="layout"
                    width={width}
                    layout={sectionLayout}
                    onLayoutChange={(l) => onSectionLayoutChange(sec.widgets, l)}
                    gridConfig={{
                      cols: responsiveCols,
                      rowHeight: ROW_HEIGHT,
                      margin: MARGIN,
                      containerPadding: [0, 0],
                      maxRows: Infinity,
                    }}
                    dragConfig={{
                      enabled: isEditing,
                      bounded: false,
                      handle: '.drag-handle',
                      threshold: 3,
                    }}
                    resizeConfig={{
                      enabled: isEditing,
                      handles: ['se', 's', 'e'],
                    }}
                    compactor={verticalCompactor}
                  >
                    {sec.widgets.map((w) => {
                      const def = getWidgetDefinition(w.widgetType);
                      const title = widgetTitle(w);
                      const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
                      return (
                        <div key={w.id} className="w-full h-full min-w-0 min-h-0" data-grid={{ x: w.position?.x, y: w.position?.y, w: w.size?.w, h: w.size?.h }}>
                          <DraggableWidget
                            id={w.id}
                            title={title}
                            isEditing={isEditing}
                            onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                            onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                            dragHandle={isEditing ? <span className="drag-handle">{dragHandle}</span> : undefined}
                            dashboardFilterSupport={def?.dashboardFilterSupport}
                          >
                            {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                          </DraggableWidget>
                        </div>
                      );
                    })}
                  </GridLayout>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : (
        <div
          ref={containerRef as React.RefObject<HTMLDivElement>}
          className={`flex-1 min-h-full rounded-xl w-full min-w-0 ${isEditing ? 'py-4' : ''}`}
          style={isEditing ? gridDotStyle : undefined}
        >
          {mounted && width > 0 && (
            <GridLayout
              className="layout"
              width={width}
              layout={displayLayout}
              onLayoutChange={onLayoutChange}
              gridConfig={{
                cols: responsiveCols,
                rowHeight: ROW_HEIGHT,
                margin: MARGIN,
                containerPadding: MARGIN,
                maxRows: Infinity,
              }}
              dragConfig={{
                enabled: isEditing,
                bounded: false,
                handle: '.drag-handle',
                threshold: 3,
              }}
              resizeConfig={{
                enabled: isEditing,
                handles: ['se', 's', 'e'],
              }}
              compactor={verticalCompactor}
            >
          {effectiveWidgets.map((w) => {
            const def = getWidgetDefinition(w.widgetType);
            const title = widgetTitle(w);
            const content = createWidgetElement(w.widgetType, w.settings ?? {}, isEditing, dashboardFilters);
            return (
              <div key={w.id} className="w-full h-full min-w-0 min-h-0" data-grid={{ x: w.position?.x, y: w.position?.y, w: w.size?.w, h: w.size?.h }}>
                <DraggableWidget
                  id={w.id}
                  title={title}
                  isEditing={isEditing}
                  onRemove={isEditing ? () => handleRemove(w.id) : undefined}
                  onSettingsClick={isEditing ? () => handleSettingsClick(w.id) : undefined}
                  dragHandle={isEditing ? <span className="drag-handle">{dragHandle}</span> : undefined}
                  dashboardFilterSupport={def?.dashboardFilterSupport}
                >
                  {content ?? <span className={colors.textMuted}>Unknown widget: {w.widgetType}</span>}
                </DraggableWidget>
              </div>
            );
          })}
        </GridLayout>
          )}
        </div>
      )}
      </div>

      <DashboardSettings
        isOpen={settingsOpen}
        onClose={closeSettings}
        layoutName={layoutName}
        editingWidgetId={editingWidgetId}
        onClearEditingWidget={() => setEditingWidgetId(null)}
      />
    </div>
  );
};

export default DynamicDashboard;
